/**
 * KgEntityDetail — /:type/:slug
 * Generic renderer for any Knowledge Graph entity. Fetches with
 * `find-or-create` semantics (viewing an unseen entity kicks off AI
 * generation and shows a loading state).
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Loader2, ShieldAlert, Sparkles, MapPin, Calendar, ExternalLink, Globe, Users,
} from "lucide-react";
import { http } from "@/lib/api";
import KgEntityTabs from "@/components/KgEntityTabs";
import KgRelatedSidebar from "@/components/KgRelatedSidebar";
import AutoLinkText from "@/components/AutoLinkText";

const TYPE_LABELS = {
  person: "Person", place: "Place", organization: "Organization",
  event: "Event", disease: "Disease / Condition",
};

export default function KgEntityDetail({ entityType }) {
  const { slug, name } = useParams();
  const [entity, setEntity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        if (slug) {
          const r = await http.get(`/kg/entities/${slug}`);
          if (!cancelled) setEntity(r.data);
          if (r.data?.status !== "ready") {
            // slug exists but body empty → kick AI generation
            const kick = await http.post("/kg/find-or-create", {
              name: r.data.name, entity_type: r.data.entity_type,
            }).catch(() => null);
            if (!cancelled && kick?.data?.entity) setEntity(kick.data.entity);
          }
        } else if (name) {
          const r = await http.post("/kg/find-or-create", {
            name: decodeURIComponent(name), entity_type: entityType,
          });
          if (!cancelled) setEntity(r.data.entity);
          if (!r.data?.ok && r.data?.entity?.status === "failed") {
            setError("AI generation failed. Try again in a moment.");
          }
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Failed to load entity.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, name, entityType]);

  const label = TYPE_LABELS[entity?.entity_type || entityType] || entity?.entity_type;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="kg-entity-detail">
      <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 mb-2">
        {label}{entity?.body?.place_type ? ` · ${entity.body.place_type}` : ""}{entity?.body?.event_type ? ` · ${entity.body.event_type}` : ""}
      </div>
      <h1 className="font-display text-4xl mb-2">{entity?.name || decodeURIComponent(name || slug || "")}</h1>
      {entity?.aliases?.length > 0 && (
        <div className="text-sm opacity-70 mb-3">
          Also known as: {entity.aliases.slice(0, 5).join(" · ")}
        </div>
      )}

      {entity?.entity_type === "disease" && (
        <div className="text-xs opacity-70 inline-flex items-start gap-1.5 mb-4">
          <ShieldAlert className="w-3.5 h-3.5 mt-0.5"/>
          Educational only — not medical advice. Always consult a qualified healthcare professional.
        </div>
      )}

      {loading && (
        <div className="wofy-card !p-6 opacity-80 inline-flex items-center gap-2" data-testid="kg-loading">
          <Loader2 className="w-4 h-4 animate-spin"/>
          Preparing an AI-authored profile… (first visit only, up to a minute)
        </div>
      )}

      {!loading && error && (
        <div className="wofy-card !p-6" style={{ background: "#FEF3C7" }}>{error}</div>
      )}

      {!loading && entity && (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6">
          <div className="min-w-0">
            <KgEntityTabs entity={entity}>
              {entity.body
                ? <EntityBody type={entity.entity_type} body={entity.body} />
                : <div className="wofy-card !p-6 opacity-80" data-testid="kg-empty">Profile still being prepared. Please reload in a moment.</div>}
            </KgEntityTabs>
          </div>
          <div className="lg:sticky lg:top-24 self-start">
            <KgRelatedSidebar slug={entity.slug} entityType={entity.entity_type} limit={6} />
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <section className="mb-6">
      <h2 className="font-display text-xl mb-2 inline-flex items-center gap-1.5">
        {Icon && <Icon className="w-4 h-4"/>}{title}
      </h2>
      {children}
    </section>
  );
}
function List({ items }) {
  if (!items?.length) return null;
  return <ul className="list-disc ml-5 space-y-1 opacity-90">{items.map((s, i) => <li key={i}>{typeof s === "string" ? s : JSON.stringify(s)}</li>)}</ul>;
}
function Timeline({ items }) {
  if (!items?.length) return null;
  return (
    <ol className="border-l pl-4 space-y-2" style={{ borderColor: "var(--wofy-border)" }}>
      {items.map((t, i) => (
        <li key={i} className="relative">
          <span className="absolute -left-[21px] top-1 w-2 h-2 rounded-full" style={{ background: "var(--wofy-primary)" }}/>
          <div className="text-xs font-mono opacity-70">{t.year || t.date}</div>
          <div className="text-sm">{t.event}</div>
        </li>
      ))}
    </ol>
  );
}
function FAQs({ faqs }) {
  if (!faqs?.length) return null;
  return (
    <div className="space-y-3">
      {faqs.map((f, i) => (
        <div key={i} className="wofy-card !p-4">
          <div className="font-medium mb-1">{f.q}</div>
          <p className="opacity-80 text-sm">{f.a}</p>
        </div>
      ))}
    </div>
  );
}
function KV({ obj, keys }) {
  if (!obj) return null;
  const rows = keys.map(k => [k[0], obj[k[1]]]).filter(([, v]) => v != null && v !== "" && !(Array.isArray(v) && v.length === 0));
  if (!rows.length) return null;
  return (
    <div className="wofy-card !p-4 grid gap-2 sm:grid-cols-2 mb-4">
      {rows.map(([label, v]) => (
        <div key={label}>
          <div className="text-xs opacity-70">{label}</div>
          <div className="text-sm">{Array.isArray(v) ? v.join(", ") : String(v)}</div>
        </div>
      ))}
    </div>
  );
}

function EntityBody({ type, body }) {
  if (type === "person") {
    return (
      <div data-testid="kg-body">
        {body.short_summary && <p className="text-lg opacity-90 mb-4">{body.short_summary}</p>}
        <KV obj={body.basic} keys={[["Nationality","nationality"],["Gender","gender"],["Languages","languages"],["Honorifics","honorifics"]]} />
        <KV obj={body.life}  keys={[["Born","date_of_birth"],["Birthplace","place_of_birth"],["Died","date_of_death"],["Died at","place_of_death"]]} />
        {body.long_biography && <Section title="Biography"><p className="whitespace-pre-line opacity-90"><AutoLinkText text={body.long_biography} perEntityMax={2} /></p></Section>}
        {body.biography?.early_life && <Section title="Early life"><p className="opacity-90">{body.biography.early_life}</p></Section>}
        {body.biography?.education  && <Section title="Education"><p className="opacity-90">{body.biography.education}</p></Section>}
        {body.biography?.career     && <Section title="Career"><p className="opacity-90">{body.biography.career}</p></Section>}
        {body.biography?.legacy     && <Section title="Legacy"><p className="opacity-90">{body.biography.legacy}</p></Section>}
        {body.achievements?.awards        && <Section title="Awards & honours"><List items={body.achievements.awards}/></Section>}
        {body.achievements?.major_works   && <Section title="Major works"><List items={body.achievements.major_works}/></Section>}
        {body.achievements?.discoveries   && <Section title="Discoveries"><List items={body.achievements.discoveries}/></Section>}
        {body.social_impact               && <Section title="Social impact"><List items={body.social_impact}/></Section>}
        {body.timeline                    && <Section title="Timeline" icon={Calendar}><Timeline items={body.timeline}/></Section>}
        {body.interesting_facts           && <Section title="Interesting facts"><List items={body.interesting_facts}/></Section>}
        {body.faqs                        && <Section title="FAQs"><FAQs faqs={body.faqs}/></Section>}
      </div>
    );
  }
  if (type === "place") {
    return (
      <div data-testid="kg-body">
        {body.short_summary && <p className="text-lg opacity-90 mb-4">{body.short_summary}</p>}
        <KV obj={body} keys={[["Country","country"],["State/Region","state"],["City","city_or_region"],["Population","population"],["Climate","climate"]]} />
        {body.long_description && <Section title="About" icon={MapPin}><p className="whitespace-pre-line opacity-90"><AutoLinkText text={body.long_description} perEntityMax={2} /></p></Section>}
        {body.history          && <Section title="History"><List items={body.history}/></Section>}
        {body.culture          && <Section title="Culture"><List items={body.culture}/></Section>}
        {body.economy          && <Section title="Economy"><List items={body.economy}/></Section>}
        {body.tourism          && <Section title="Tourism & attractions"><List items={body.tourism}/></Section>}
        {body.languages        && <Section title="Languages"><List items={body.languages}/></Section>}
        {body.transportation   && <Section title="Transportation"><List items={body.transportation}/></Section>}
        {body.timeline         && <Section title="Timeline" icon={Calendar}><Timeline items={body.timeline}/></Section>}
        {body.interesting_facts&& <Section title="Interesting facts"><List items={body.interesting_facts}/></Section>}
        {body.external_links && (Object.keys(body.external_links).length > 0) && (
          <Section title="External links" icon={ExternalLink}>
            <div className="space-y-1">{Object.entries(body.external_links).map(([k,v]) => v && <a key={k} href={v} target="_blank" rel="noopener noreferrer" className="text-sm wofy-link block">{k}: {v}</a>)}</div>
          </Section>
        )}
        {body.faqs             && <Section title="FAQs"><FAQs faqs={body.faqs}/></Section>}
      </div>
    );
  }
  if (type === "organization") {
    return (
      <div data-testid="kg-body">
        {body.short_summary && <p className="text-lg opacity-90 mb-4">{body.short_summary}</p>}
        <KV obj={body} keys={[["Type","org_type"],["Industry","industry"],["Founded","founded"],["Founders","founders"],["Headquarters","headquarters"],["Website","website"]]} />
        {body.long_description && <Section title="About" icon={Users}><p className="whitespace-pre-line opacity-90">{body.long_description}</p></Section>}
        {body.notable_work      && <Section title="Notable work"><List items={body.notable_work}/></Section>}
        {body.leadership_notable && <Section title="Notable leadership"><List items={body.leadership_notable}/></Section>}
        {body.awards            && <Section title="Awards"><List items={body.awards}/></Section>}
        {body.history           && <Section title="History"><List items={body.history}/></Section>}
        {body.timeline          && <Section title="Timeline" icon={Calendar}><Timeline items={body.timeline}/></Section>}
        {body.interesting_facts && <Section title="Interesting facts"><List items={body.interesting_facts}/></Section>}
        {body.faqs              && <Section title="FAQs"><FAQs faqs={body.faqs}/></Section>}
      </div>
    );
  }
  if (type === "event") {
    return (
      <div data-testid="kg-body">
        {body.short_summary && <p className="text-lg opacity-90 mb-4">{body.short_summary}</p>}
        <KV obj={body} keys={[["Type","event_type"],["Start","start_date"],["End","end_date"],["Location","location"]]} />
        {body.long_description && <Section title="About"><p className="whitespace-pre-line opacity-90"><AutoLinkText text={body.long_description} perEntityMax={2} /></p></Section>}
        {body.background        && <Section title="Background"><List items={body.background}/></Section>}
        {body.key_participants  && <Section title="Key participants"><List items={body.key_participants}/></Section>}
        {body.outcome           && <Section title="Outcome"><List items={body.outcome}/></Section>}
        {body.impact            && <Section title="Impact"><List items={body.impact}/></Section>}
        {body.timeline          && <Section title="Timeline" icon={Calendar}><Timeline items={body.timeline}/></Section>}
        {body.interesting_facts && <Section title="Interesting facts"><List items={body.interesting_facts}/></Section>}
        {body.faqs              && <Section title="FAQs"><FAQs faqs={body.faqs}/></Section>}
      </div>
    );
  }
  if (type === "disease") {
    return (
      <div data-testid="kg-body">
        {body.overview          && <Section title="Overview"><p className="opacity-90 whitespace-pre-line"><AutoLinkText text={body.overview} perEntityMax={2} /></p></Section>}
        {body.symptoms          && <Section title="Symptoms"><List items={body.symptoms}/></Section>}
        {body.causes            && <Section title="Causes"><List items={body.causes}/></Section>}
        {body.risk_factors      && <Section title="Risk factors"><List items={body.risk_factors}/></Section>}
        {body.prevention        && <Section title="Prevention"><List items={body.prevention}/></Section>}
        {body.diagnosis         && <Section title="Diagnosis"><List items={body.diagnosis}/></Section>}
        {body.treatment         && <Section title="Treatment (general categories)"><List items={body.treatment}/></Section>}
        {body.complications     && <Section title="Complications"><List items={body.complications}/></Section>}
        {body.recovery          && <Section title="Recovery"><List items={body.recovery}/></Section>}
        {body.lifestyle         && <Section title="Lifestyle"><List items={body.lifestyle}/></Section>}
        {body.when_to_seek_care && <Section title="When to seek care"><p className="whitespace-pre-line" style={{ color: "var(--wofy-primary)" }}>{body.when_to_seek_care}</p></Section>}
        {body.related_specialists && <Section title="Related specialists"><List items={body.related_specialists}/></Section>}
        {body.faqs              && <Section title="FAQs"><FAQs faqs={body.faqs}/></Section>}
      </div>
    );
  }
  return <pre className="wofy-card !p-4 text-xs overflow-auto">{JSON.stringify(body, null, 2)}</pre>;
}
